// Site content for Sigma Enterprises — ported from the approved design.

export const contact = {
  phone: "+91 99992 42566",
  // WhatsApp number in international format, digits only (country code + number).
  // Enquiries from the contact form and product "Enquire" buttons go here.
  whatsapp: "919999242566",
  address: ["B-33/13, Paper Market,", "Ramesh Nagar, Raja Garden,", "New Delhi — 110015"],
  hours: "Mon – Sat · 10:00 – 20:00",
  city: "New Delhi, India",
  // Location for the embedded map on the Contact page.
  mapEmbed:
    "https://www.google.com/maps?q=28.6499373,77.1271445&z=16&output=embed",
  mapLink: "https://maps.app.goo.gl/HkBcHffLwKRvUgNH7",
};

// Product image for every adhesive grade (all share one photo). The alt text
// is written per grade rather than shared, so each card describes the product
// the reader is actually looking at.
const ADHESIVE_IMG = "/assets/adhesive.webp";
const adhesiveAlt = (grade) =>
  `${grade} self-adhesive label stock with its backing liner peeled back`;

// Every image carries an `alt` describing what is in the photograph. Alt text
// is read aloud by screen readers and shown when an image fails, so it must
// describe the object — not repeat the product name, and never carry search
// keywords like city or "wholesale supplier".
export const categories = [
  {
    slug: "coated-papers-boards",
    name: "Coated Papers & Boards",
    count: 3,
    blurb: "Smooth, high-brightness surfaces for premium print and packaging.",
    image: "/assets/nevia-art-card-gloss.webp",
    imageAlt: "Stack of gloss art card in its printed mill wrapper",
    items: [
      {
        name: "SBS Board",
        slug: "sbs-board",
        tag: "190–350 gsm",
        image: "/assets/ningbo-fold-fbb-packaging-board.webp",
        // The photograph is of folding box board, which is FBB — a different
        // grade from the SBS this card describes. Until a real SBS photo is
        // supplied the alt text stays generic rather than asserting a grade
        // the image does not show.
        alt: "Strapped bundles of white packaging board",
        desc: "Coated one side only. Available in Natural or White base — clean, rigid stock for food-grade and premium packaging.",
      },
      {
        name: "Art Card",
        slug: "art-card",
        tag: "210–350 gsm",
        image: "/assets/nevia-art-card-gloss.webp",
        alt: "Stack of gloss art card in its printed mill wrapper",
        desc: "Coated both sides, white only. Heavyweight board for covers, cartons and premium packaging.",
      },
      {
        name: "Art Paper",
        slug: "art-paper",
        tag: "80–170 gsm",
        image: "/assets/art-paper.webp",
        alt: "Wrapped ream of gloss-coated art paper",
        desc: "Coated both sides, white only. Bright gloss sheet for brochures, inserts and premium print.",
      },
    ],
  },
  {
    slug: "uncoated-printing",
    name: "Uncoated & Printing",
    count: 5,
    blurb: "Everyday printing and writing stock in a wide range of grammages.",
    image: "/assets/printing-paper.webp",
    imageAlt: "Wrapped reams of offset printing paper stacked on a pallet",
    items: [
      {
        name: "Uncoated Paper",
        slug: "uncoated-paper",
        tag: "natural",
        image: "/assets/uncoated.webp",
        alt: "Stack of natural uncoated paper sheets",
        desc: "Matte, easy-to-write surface for general use.",
      },
      {
        name: "Printing Paper",
        slug: "printing-paper",
        tag: "offset",
        image: "/assets/printing-paper.webp",
        alt: "Wrapped reams of offset printing paper stacked on a pallet",
        desc: "Reliable offset stock for books and commercial print.",
      },
      {
        name: "Copier Paper",
        slug: "copier-paper",
        tag: "70–100 gsm",
        image: "/assets/copier.webp",
        alt: "Pallets of boxed copier paper reams in a warehouse",
        desc: "Multipurpose office reams in bulk.",
      },
      {
        name: "Sunshine Paper",
        slug: "sunshine-paper",
        tag: "super-white",
        image: "/assets/sunshine.webp",
        alt: "Wrapped packs of super-white sunshine maplitho paper",
        desc: "High-white maplitho for crisp text reproduction.",
      },
      {
        name: "Textured Paper",
        slug: "textured-paper",
        tag: "50+ textures",
        image: "/assets/textured.webp",
        alt: "Fanned sheets of textured paper in assorted finishes",
        desc: "Felt, laid, linen and more — available in 120 gsm paper or 300 gsm board weight.",
      },
    ],
  },
  {
    slug: "kraft-packaging-boards",
    name: "Kraft & Packaging Boards",
    count: 3,
    blurb:
      "Strong, durable boards for cartons, cores and heavy-duty packaging.",
    image: "/assets/kraft.webp",
    imageAlt: "Fanned sheets of brown kraft paper",
    items: [
      {
        name: "Kraft Paper & Board",
        slug: "kraft-paper-board",
        tag: "high burst",
        image: "/assets/kraft.webp",
        alt: "Fanned sheets of brown kraft paper",
        desc: "Tough, high-strength stock for packaging and wrapping.",
      },
      {
        name: "Duplex Board",
        slug: "duplex-board",
        tag: "grey / white back",
        image: "/assets/duplex.webp",
        alt: "Duplex board sheets fanned out, showing the grey back and white top",
        desc: "Economical board for folding cartons.",
      },
      {
        name: "Kappa Board",
        slug: "kappa-board",
        tag: "rigid",
        image: "/assets/kappa.webp",
        alt: "Fanned sheets of rigid grey kappa board",
        desc: "Dense grey board for rigid boxes and binding.",
      },
    ],
  },
  {
    slug: "specialty-papers",
    name: "Specialty Papers",
    count: 4,
    blurb:
      "Engineered papers for technical, durable and specialist applications.",
    image: "/assets/tracing.webp",
    imageAlt: "Translucent tracing paper sheet",
    items: [
      {
        name: "Synthetic Paper",
        slug: "synthetic-paper",
        tag: "waterproof",
        image: "/assets/synthetic.webp",
        alt: "Mill label on a wrapped reel of waterproof synthetic paper",
        desc: "Tear- and water-resistant for outdoor and durable labels.",
      },
      {
        name: "Non-Tearable Paper",
        slug: "non-tearable-paper",
        tag: "tear-proof",
        image: "/assets/non-tearable.webp",
        alt: "Mill label on a wrapped reel of non-tearable paper",
        desc: "Rugged stock for tags, maps and heavy-use documents.",
      },
      {
        name: "Tracing Paper",
        slug: "tracing-paper",
        tag: "translucent",
        image: "/assets/tracing.webp",
        alt: "Translucent tracing paper sheet",
        desc: "Smooth translucent sheet for design and technical work.",
      },
      {
        name: "Tissue Paper",
        slug: "tissue-paper",
        tag: "lightweight",
        image: "/assets/tissue.webp",
        alt: "Folded sheets of lightweight white tissue paper",
        desc: "Soft, fine tissue for wrapping and interleaving.",
      },
    ],
  },
  {
    slug: "coloured-specialty-stock",
    name: "Coloured & Specialty Stock",
    count: 2,
    blurb: "Bold colours and dense blacks for creative and premium projects.",
    image: "/assets/color.webp",
    imageAlt: "Coloured paper sheets fanned out in assorted shades",
    items: [
      {
        name: "Colour Paper",
        slug: "colour-paper",
        tag: "assorted",
        image: "/assets/color.webp",
        alt: "Coloured paper sheets fanned out in assorted shades",
        desc: "Vivid coloured sheets for craft and print.",
      },
      {
        name: "Black Paper",
        slug: "black-paper",
        tag: "deep matt",
        image: "/assets/black.webp",
        alt: "Curled sheet of deep matt black paper",
        desc: "Dense black stock for premium packaging and mounts.",
      },
    ],
  },
  {
    slug: "adhesive-paper",
    name: "Adhesive Paper",
    count: 5,
    blurb:
      "Self-adhesive face stocks for labels, stickers and packaging — coated, film and natural options.",
    image: ADHESIVE_IMG,
    imageAlt: adhesiveAlt("Coated"),
    items: [
      {
        name: "Chromo",
        slug: "chromo-self-adhesive",
        tag: "gloss coated",
        image: ADHESIVE_IMG,
        alt: adhesiveAlt("Gloss chromo"),
        desc: "Bright coated face stock for high-quality printed labels.",
      },
      {
        name: "PVC",
        slug: "pvc-self-adhesive",
        tag: "waterproof",
        image: ADHESIVE_IMG,
        alt: adhesiveAlt("Waterproof PVC film"),
        desc: "Durable plastic film for water- and tear-resistant labels.",
      },
      {
        name: "Woodfree",
        slug: "woodfree-self-adhesive",
        tag: "uncoated",
        image: ADHESIVE_IMG,
        alt: adhesiveAlt("Uncoated woodfree"),
        desc: "Matte, writable face stock for general-purpose labels.",
      },
      {
        name: "Vinyl",
        slug: "vinyl-self-adhesive",
        tag: "weatherproof",
        image: ADHESIVE_IMG,
        alt: adhesiveAlt("Weatherproof vinyl film"),
        desc: "Flexible film for outdoor, curved and long-life labelling.",
      },
      {
        name: "Kraft",
        slug: "kraft-self-adhesive",
        tag: "natural brown",
        image: ADHESIVE_IMG,
        alt: adhesiveAlt("Natural brown kraft"),
        desc: "Natural brown adhesive stock for rustic and eco packaging labels.",
      },
    ],
  },
];

export const values = [
  {
    t: "Wide Range",
    d: "From copier reams to rigid board — 20+ categories under one roof.",
  },
  {
    t: "Trusted Supply",
    d: "Ready inventory and dependable, on-time delivery across India.",
  },
  {
    t: "Quality Assured",
    d: "Consistent grammage, brightness and finish in every consignment.",
  },
  {
    t: "Trade Pricing",
    d: "Competitive wholesale rates for printers, converters and resellers.",
  },
];

// What the trade gets, stated plainly.
//
// This section previously held three testimonials attributed to named people at
// named companies. They were placeholder copy, not real customers, so they have
// been replaced: presenting invented quotes as real customer endorsements is
// deceptive to buyers and, under Google's spam policies, a genuine risk to the
// site. Real social proof belongs on the Google Business Profile, where reviews
// are verifiable and where they also feed local ranking — see
// seo/local-seo-playbook.md.
//
// Every claim below must stay factually true of the business. Nothing here
// asserts a certification, a client name, or a number we cannot stand behind.
export const assurances = [
  {
    t: "Three decades in the trade",
    d: "Sigma Enterprises has supplied paper and board from New Delhi since 1996. Buyers deal with people who know the difference a grade makes on press, not a call centre.",
  },
  {
    t: "One source, twenty-plus lines",
    d: "Copier reams, offset and maplitho, art card and art paper, SBS, kraft, duplex, kappa, synthetic, tracing, coloured, black and self-adhesive face stocks — consolidated into a single order and a single delivery.",
  },
  {
    t: "Straight answers on availability",
    d: "Mill stock moves. Call with a grade, grammage and quantity and you get current availability and a trade price — not a quote against stock that has to be chased afterwards.",
  },
];

export const marquee = [
  "Coated Paper",
  "Art Card",
  "SBS",
  "Kraft Board",
  "Duplex",
  "Copier",
  "Synthetic",
  "Tracing",
  "Tissue",
  "Colour",
  "Kappa",
  "Adhesive",
];

// Brand logos for the moving strip on the home page (files in public/assets/logos).
//
// `w` / `h` are the intrinsic pixel dimensions of the generated WebP, written
// onto the <img> as width/height attributes. The strip renders every logo at a
// fixed 40px height with width:auto, so without an intrinsic aspect ratio the
// browser reserves zero width until each file decodes and the whole row jumps
// sideways as they arrive — a pure layout-shift cost. These come from
// scripts/optimize-images.js (logo profile: fit inside 260x80).
export const logos = [
  { src: "/assets/logos/jk.webp", name: "JK Paper", w: 107, h: 80 },
  { src: "/assets/logos/paperone.webp", name: "PaperOne", w: 202, h: 80 },
  { src: "/assets/logos/century.webp", name: "Century", w: 130, h: 80 },
  { src: "/assets/logos/trident.webp", name: "Trident", w: 107, h: 80 },
  { src: "/assets/logos/app.webp", name: "APP", w: 80, h: 80 },
  { src: "/assets/logos/april.webp", name: "APRIL", w: 120, h: 80 },
  { src: "/assets/logos/cosmo.webp", name: "Cosmo", w: 142, h: 80 },
  { src: "/assets/logos/nevia.webp", name: "Nevia", w: 120, h: 80 },
  { src: "/assets/logos/ningbo.webp", name: "Ningbo", w: 107, h: 80 },
  { src: "/assets/logos/golden-coin.webp", name: "Golden Coin", w: 160, h: 80 },
  { src: "/assets/logos/hi-cote.webp", name: "Hi-Cote", w: 120, h: 80 },
  { src: "/assets/logos/avery-dennison.webp", name: "Avery Dennison", w: 180, h: 80 },
];
